import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

interface StatusStore {
  clickedStatus: string | null;
  clickedAssignees: string[];
  clickedTags: string[];
  clickedOrganizerStatus: string | null;
  readyToGo: boolean;
  sortBy: string | undefined;
  order: "asc" | "desc";
  limit: number;
  showArchivedClients: boolean;
  searchQuery: string;
  selectedTaxYear: number | null;
  availableTaxYears: number[];
  setClickedStatus: (status: string | null) => void;
  setClickedAssignees: (assignees: string[]) => void;
  setClickedTags: (tags: string[]) => void;
  setClickedOrganizerStatus: (status: string | null) => void;
  setReadyToGo: (readyToGo: boolean) => void;
  setSortBy: (sortBy: string | undefined) => void;
  setOrder: (order: "asc" | "desc") => void;
  setLimit: (limit: number) => void;
  setShowArchivedClients: (show: boolean) => void;
  setSearchQuery: (query: string) => void;
  setSelectedTaxYear: (year: number | null) => void;
  setAvailableTaxYears: (years: number[]) => void;
}

export const useStatusStore = create<StatusStore>()(
  persist(
    (set) => ({
      clickedStatus: null,
      clickedAssignees: [],
      clickedTags: [],
      clickedOrganizerStatus: null,
      readyToGo: false,
      sortBy: "updatedAt",
      order: "desc",
      limit: 10,
      showArchivedClients: false,
      searchQuery: "",
      selectedTaxYear: null,
      availableTaxYears: [],
      setClickedStatus: (status: string | null) =>
        set({ clickedStatus: status }),
      setClickedAssignees: (assignees: string[]) =>
        set({ clickedAssignees: assignees }),
      setClickedTags: (tags: string[]) => set({ clickedTags: tags }),
      setClickedOrganizerStatus: (status: string | null) =>
        set({ clickedOrganizerStatus: status }),
      setReadyToGo: (readyToGo: boolean) => set({ readyToGo: readyToGo }),
      setSortBy: (sortBy: string | undefined) => set({ sortBy }),
      setOrder: (order: "asc" | "desc") => set({ order }),
      setLimit: (limit: number) => set({ limit }),
      setShowArchivedClients: (show: boolean) =>
        set({ showArchivedClients: show }),
      setSearchQuery: (query: string) => set({ searchQuery: query }),
      setSelectedTaxYear: (year: number | null) =>
        set({ selectedTaxYear: year }),
      setAvailableTaxYears: (years: number[]) =>
        set({ availableTaxYears: years }),
    }),
    {
      name: "status-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        clickedAssignees: state.clickedAssignees,
        clickedTags: state.clickedTags,
        clickedStatus: state.clickedStatus,
        clickedOrganizerStatus: state.clickedOrganizerStatus,
        readyToGo: state.readyToGo,
        sortBy: state.sortBy,
        order: state.order,
        searchQuery: state.searchQuery,
        selectedTaxYear: state.selectedTaxYear,
        // Explicitly not persisted:
        // - showArchivedClients: always default to false on page load
        // - limit: use default value on each session
        // - availableTaxYears: fetched from API
      }),
      // Merge persisted state with defaults, ensuring defaults are used for undefined values
      merge: (persistedState: any, currentState: StatusStore) => ({
        ...currentState,
        ...persistedState,
        // Ensure defaults for undefined values
        sortBy: persistedState?.sortBy ?? currentState.sortBy,
        order: persistedState?.order ?? currentState.order,
        limit: persistedState?.limit ?? currentState.limit,
        // showArchivedClients not persisted, will always use default (false)
        searchQuery: persistedState?.searchQuery ?? currentState.searchQuery,
        clickedAssignees:
          persistedState?.clickedAssignees ?? currentState.clickedAssignees,
        clickedTags: persistedState?.clickedTags ?? currentState.clickedTags,
        clickedOrganizerStatus:
          persistedState?.clickedOrganizerStatus ??
          currentState.clickedOrganizerStatus,
        selectedTaxYear:
          persistedState?.selectedTaxYear ?? currentState.selectedTaxYear,
      }),
    }
  )
);
